import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';
import Cookies from 'js-cookie';

import FormHeader from 'prequalificationV2/components/FormHeader';
import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'prequalificationV2/actions';
import {
  setApplication,
  setBorrowerChangedInfoAfterProvePreFill,
  setIsCoBorrowerManualFlow,
} from 'prequalificationV2/actions/application';
import { setBorrower } from 'prequalificationV2/actions/borrower';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import Input from 'components/Inputs/Input';
import Loader from 'components/Loader';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { completeBorrowerRegistration, updateBorrower } from 'prequalificationV2/thunks';
import {
  BORROWER_FIRST_LAST_NAME_MAX_LENGTH,
  MASKED_SSN_LENGTH,
  SSN_MAX_LENGTH,
  ZIP_CODE_MAX_LENGTH,
} from 'prequalificationV2/utils/constants';
import { identityFullStory } from 'prequalificationV2/utils/fullStory';
import {
  getStreetAddress,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
  maskSSN,
  removeNonDigits,
} from 'prequalificationV2/utils/stringUtils';
import { getMessageForInvalidFields } from 'prequalificationV2/utils/validationUtils';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import {
  getAddressCaptionMessage,
  removeIncorrectSymbols,
  validateCityField,
  validateFirstName,
  validateLastName,
} from 'prequalificationV2/pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { EyeIcon } from 'components/Icons';
import { BorrowerInformationFormValues } from 'prequalificationV2/pages/PreQualification/steps/BorrowerInformation/borrowerInformation.types';
import { BorrowerInformationLabel } from 'prequalificationV2/pages/PreQualification/steps/BorrowerInformation/borrowerInformation.enums';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { isPuertoRicoZipCode } from 'prequalificationV2/utils/address';
import { ApplicationType } from 'enums/ApplicationType';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { CONCEPTUAL_DEVICE_ID_COOKIE } from 'prequalificationV2/routes/routes.constants';

import styles from './BorrowerInformation.module.scss';

const BorrowerInformation = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const { isBorrowerAuthorized } = useAppSelector((state) => state.prequalificationPortal);
  const { application, borrower, data } = useAppSelector((store) => store);

  const {
    register,
    unregister,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    control,
    formState,
    getValues,
    handleSubmit,
    clearErrors,
  } = useForm<BorrowerInformationFormValues>({
    defaultValues: {
      borrowerFirstName: borrower.firstName,
      borrowerLastName: borrower.lastName,
      streetAddress: getStreetAddress(borrower.streetNumber, borrower.streetName),
      city: borrower.city,
      state: borrower.state,
      zipCode: borrower.zip,
      county: borrower.county || '',
      personalIdNumber: borrower.personalIdNumber,
      isHomeImprovementSameAsPrimaryAddress: borrower.isHomeImprovementSameAsPrimaryAddress ?? null,
      homeImprovementAddressStreetAddress: borrower.homeImprovementAddress?.street_name || '',
      homeImprovementAddressCity: borrower.homeImprovementAddress?.city || '',
      homeImprovementAddressState: borrower.homeImprovementAddress?.state_or_province || '',
      homeImprovementAddressZip: borrower.homeImprovementAddress?.zip_or_postal_code || '',
      loanType: application[LoanInformationVariables.LoanType],
    },
  });
  const watcher = watch();
  const [isAddressOfHomeImprovementOpened, setIsAddressOfHomeImprovementOpened] = useState(false);
  const [isSsnVisible, setSsnVisible] = useState(
    !!(!application![LoanInformationVariables.IsBorrowerManualFlow] || borrower.personalIdNumber),
  );

  const getErrorMessageForRequiredFields = (fieldLabel: string) => `${fieldLabel} is required`;

  const handleInputChange = () => {
    dispatch(setBorrowerChangedInfoAfterProvePreFill(true));
  };

  useEffect(() => {
    register('borrowerFirstName', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.FirstName),
      validate: (value) => {
        return validateFirstName(value, BorrowerInformationLabel.FirstName);
      },
    });
    register('borrowerLastName', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.LastName),
      validate: (value) => {
        return validateLastName(value, BorrowerInformationLabel.LastName);
      },
    });
    register('streetAddress', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeStreetAddress),
    });
    register('city', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.City),
      validate: (value) => {
        return validateCityField(value, BorrowerInformationLabel.City);
      },
    });
    register('state', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.State),
    });
    register('zipCode', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.ZipCode),
      validate: (value) => {
        const zipValue = value?.trim().replace(/\D/g, '');

        return zipValue.length === ZIP_CODE_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.ZipCode);
      },
    });
    register('personalIdNumber', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.SSN),
      validate: (value) => value.length === SSN_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.SSN),
    });
    register('loanType', {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanType),
    });
  }, [register, watcher]);

  useEffect(() => {
    if (watcher.isHomeImprovementSameAsPrimaryAddress === false) {
      register('homeImprovementAddressStreetAddress', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.WhereHomeImprovementsOccurring),
      });
      register('homeImprovementAddressCity', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementCity),
        validate: (value) => {
          return validateCityField(value, BorrowerInformationLabel.HomeImprovementCity);
        },
      });
      register('homeImprovementAddressState', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementState),
      });
      register('homeImprovementAddressZip', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementZipCode),
        validate: (value) => {
          const zipValue = value?.trim().replace(/\D/g, '');

          return (
            zipValue?.length === ZIP_CODE_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.ZipCode)
          );
        },
      });

      setIsAddressOfHomeImprovementOpened(true);
    } else {
      unregister([
        'homeImprovementAddressStreetAddress',
        'homeImprovementAddressCity',
        'homeImprovementAddressState',
        'homeImprovementAddressZip',
      ]);

      setIsAddressOfHomeImprovementOpened(false);
    }
  }, [register, unregister, watcher.isHomeImprovementSameAsPrimaryAddress]);

  useEffect(() => {
    const getBorrowerInfo = async () => {
      if (!!application![LoanInformationVariables.IsBorrowerManualFlow]) {
        await dispatch(
          setBorrower({
            eligible: false,
          }),
        );
      }
    };

    getBorrowerInfo();

    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Borrower,
          prequalificationVersion: PrequalificationVersion.Second,
          ...(borrower.borrowerId && { borrowerId: borrower.borrowerId }),
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const goToNextStep = () => {
    if (watcher.loanType !== ApplicationType.Individual) {
      dispatch(setIsCoBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
    }
  };

  useEffect(() => {
    if (!watcher.state || watcher.state.length !== 2) {
      setValue('state', '');
    }
  }, []);

  const updateBorrowerData = async () => {
    await dispatch(
      updateBorrower({
        firstName: watcher.borrowerFirstName.trim(),
        lastName: watcher.borrowerLastName.trim(),
        address: {
          streetName: getStreetNameFromStreetAddress(watcher.streetAddress),
          streetNumber: getStreetNumberFromStreetAddress(watcher.streetAddress),
          city: watcher.city,
          stateOrProvince: watcher.state,
          zipOrPostalCode: watcher.zipCode,
          county: watcher.county,
        },
        dateOfBirth: borrower.dateOfBirth,
        phone: borrower.phone,
        email: borrower.email,
      }),
    ).unwrap();
  };

  const handleOnSubmit = handleSubmit(async () => {
    const result = await trigger();

    if (isPuertoRicoZipCode(watcher.zipCode) || isPuertoRicoZipCode(watcher.homeImprovementAddressZip)) {
      notificationManager.notifyFailure(FailureNotificationType.RestrictedZipCode);

      return;
    }

    if (result) {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      const homeImprovementAddress = !watcher.isHomeImprovementSameAsPrimaryAddress
        ? {
            street_name: watcher.homeImprovementAddressStreetAddress,
            city: watcher.homeImprovementAddressCity,
            state_or_province: watcher.homeImprovementAddressState,
            zip_or_postal_code: watcher.homeImprovementAddressZip,
          }
        : {
            street_name: watcher.streetAddress,
            city: watcher.city,
            state_or_province: watcher.state,
            zip_or_postal_code: watcher.zipCode,
          };

      dispatch(
        setBorrower({
          firstName: watcher.borrowerFirstName.trim(),
          lastName: watcher.borrowerLastName.trim(),
          streetName: getStreetNameFromStreetAddress(watcher.streetAddress),
          streetNumber: getStreetNumberFromStreetAddress(watcher.streetAddress),
          city: watcher.city,
          state: watcher.state,
          zip: watcher.zipCode,
          county: watcher.county,
          personalIdNumber: watcher.personalIdNumber,
          isHomeImprovementSameAsPrimaryAddress: watcher.isHomeImprovementSameAsPrimaryAddress,
          homeImprovementAddress: homeImprovementAddress,
        }),
      );
      dispatch(
        setApplication({
          [LoanInformationVariables.LoanType]: watcher.loanType as ApplicationType,
        }),
      );
      identityFullStory(borrower.email, `${watcher.borrowerFirstName}_${watcher.borrowerLastName}`);

      try {
        const borrowerShortInfo = await preQualificationApi.getBorrowerShortInfoBySsn({
          ssn: watcher.personalIdNumber,
          email: borrower.email,
        });

        //full authorized borrower flow
        if (isBorrowerAuthorized && borrowerShortInfo.id) {
          await updateBorrowerData();

          goToNextStep();

          return;
        }

        //full unauthorized borrower flow
        if (!isBorrowerAuthorized && borrowerShortInfo.id) {
          dispatch(setAccount({ id: borrowerShortInfo.id }));
          dispatch(setSignInStep(SignInStep.PrequalOtpVerification));
          navigator(AppRoutes.Login);

          return;
        }

        //intent authorized borrower flow or new borrower
        if (!borrowerShortInfo.id) {
          //New borrower case

          await dispatch(
            completeBorrowerRegistration({
              createBorrower: {
                firstName: watcher.borrowerFirstName.trim(),
                lastName: watcher.borrowerLastName.trim(),
                email: borrower.email,
                personalIdNumber: watcher.personalIdNumber,
                phone: borrower.phone,
                address: {
                  streetNumber: getStreetNumberFromStreetAddress(watcher.streetAddress),
                  streetName: getStreetNameFromStreetAddress(watcher.streetAddress),
                  city: watcher.city,
                  stateOrProvince: watcher.state,
                  county: watcher.county,
                  zipOrPostalCode: watcher.zipCode,
                },
                dateOfBirth: borrower.dateOfBirth,
                fullConsentAgreed: !!borrower.fullConsentAgreed,
                consentAgreed: true,
                deviceId: Cookies.get(CONCEPTUAL_DEVICE_ID_COOKIE),
                contractorAutofillUsed: data.contractorAutofillUsed,
              },
              isBorrowerAuthorized: isBorrowerAuthorized,
            }),
          );

          dispatch(setPrequalificationPortal({ isPrequalStepPreset: true }));

          goToNextStep();
        }
      } catch (error: any) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  });

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>THIS INQUIRY DOES NOT AFFECT YOUR CREDIT SCORE</div>
          </div>
          <FormHeader title="Borrower" percent={25} step={2} totalSteps={4} />
          <FormBody>
            <div className={styles.rowContainer}>
              <Input
                label={BorrowerInformationLabel.FirstName}
                errorMessage={errors.borrowerFirstName?.message}
                className={styles.formInput}
                name="borrowerFirstName"
                onChange={(event) => {
                  handleInputChange();
                  setValue('borrowerFirstName', event.target.value);
                  trigger('borrowerFirstName');
                }}
                value={watcher.borrowerFirstName}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
                data-hj-allow
              />
              <Input
                label={BorrowerInformationLabel.LastName}
                errorMessage={errors.borrowerLastName?.message}
                className={styles.formInput}
                name="borrowerLastName"
                onChange={(event) => {
                  handleInputChange();
                  setValue('borrowerLastName', event.target.value);
                  trigger('borrowerLastName');
                }}
                value={watcher.borrowerLastName}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
                data-hj-allow
              />
            </div>
            <div className={styles.rowContainer}>
              <Input
                label={BorrowerInformationLabel.SSN}
                className={styles.formInput}
                name="personalIdNumber"
                type={isSsnVisible ? 'text' : 'password'}
                maxLength={isSsnVisible ? MASKED_SSN_LENGTH : SSN_MAX_LENGTH}
                onChange={(event) => {
                  const value = removeNonDigits(event.target.value.trim());

                  setValue('personalIdNumber', value);

                  if (formState.isSubmitted) {
                    trigger('personalIdNumber');
                  }
                }}
                errorMessage={errors.personalIdNumber?.message}
                value={
                  application![LoanInformationVariables.IsBorrowerManualFlow] && !borrower.personalIdNumber
                    ? maskSSN(watcher.personalIdNumber, !isSsnVisible)
                    : `***-**-${watcher.personalIdNumber.substr(5)}`
                }
                disabled={!application![LoanInformationVariables.IsBorrowerManualFlow] || !!borrower.personalIdNumber}
                inputMode="numeric"
                endAdornment={() =>
                  application![LoanInformationVariables.IsBorrowerManualFlow] && !borrower.personalIdNumber ? (
                    <EyeIcon
                      open={isSsnVisible}
                      className={styles.eyeIcon}
                      onClick={() => {
                        setSsnVisible(!isSsnVisible);
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            </div>
            <div className={styles.addressHeader}>Primary Address</div>
            <SearchLocationInput
              control={control}
              required
              useFormControl
              inputNames={{
                streetAddress: 'streetAddress',
                city: 'city',
                state: 'state',
                zip: 'zipCode',
                county: 'county',
              }}
              caption={getAddressCaptionMessage(BorrowerInformationLabel.HomeStreetAddress)}
              errors={{
                streetAddress: errors.streetAddress,
                city: errors.city,
                state: errors.state,
                zip: errors.zipCode,
              }}
              streetLabel={BorrowerInformationLabel.HomeStreetAddress}
              initialValue={{
                streetAddress: watcher.streetAddress,
                city: watcher.city,
                state: watcher.state,
                zip: watcher.zipCode,
              }}
              onStateChange={handleInputChange}
              inputChange={(name, value) => {
                clearErrors(name as keyof BorrowerInformationFormValues);
                handleInputChange();

                if (name === 'streetAddress') {
                  setValue(name as keyof BorrowerInformationFormValues, removeIncorrectSymbols(value));
                } else {
                  setValue(name as keyof BorrowerInformationFormValues, value);
                }

                if (formState.isSubmitted) {
                  trigger(name as keyof BorrowerInformationFormValues);
                }
              }}
              getValues={getValues}
            />
            <div className={styles.addressHeader}>Address of Home Improvement</div>
            <div>
              <div className={styles.radioContainer}>
                <div className={styles.subheader}>
                  Is the Primary Address where the home improvement work will take place?
                </div>
                <FormControl>
                  <Controller
                    name="isHomeImprovementSameAsPrimaryAddress"
                    control={control}
                    rules={{
                      validate: (value) => {
                        return value !== null;
                      },
                    }}
                    render={() => (
                      <RadioGroup
                        row
                        value={watcher.isHomeImprovementSameAsPrimaryAddress}
                        onChange={(event) => {
                          setValue('isHomeImprovementSameAsPrimaryAddress', JSON.parse(event.target.value));

                          if (isSubmitted) {
                            trigger();
                          }
                        }}
                      >
                        <FormControlLabel
                          classes={{ label: styles.radioButtonLabel }}
                          value={true}
                          control={
                            <Radio
                              classes={{
                                root: styles.radioButton,
                              }}
                            />
                          }
                          label={BorrowerInformationLabel.HomeImprovementYes}
                        />
                        <FormControlLabel
                          classes={{ label: styles.radioButtonLabel }}
                          value={false}
                          control={
                            <Radio
                              classes={{
                                root: styles.radioButton,
                              }}
                            />
                          }
                          label={BorrowerInformationLabel.HomeImprovementNo}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </div>
              {!!errors?.isHomeImprovementSameAsPrimaryAddress && (
                <FormHelperText className={styles.errorMessage}>
                  {BorrowerInformationLabel.HomeImprovementInformationIsRequired}
                </FormHelperText>
              )}
            </div>
            {isAddressOfHomeImprovementOpened && (
              <div className={styles.homeImprovementContainer}>
                <SearchLocationInput
                  control={control}
                  required
                  useFormControl
                  inputNames={{
                    streetAddress: 'homeImprovementAddressStreetAddress',
                    city: 'homeImprovementAddressCity',
                    state: 'homeImprovementAddressState',
                    zip: 'homeImprovementAddressZip',
                  }}
                  errors={{
                    streetAddress: errors.homeImprovementAddressStreetAddress,
                    city: errors.homeImprovementAddressCity,
                    state: errors.homeImprovementAddressState,
                    zip: errors.homeImprovementAddressZip,
                  }}
                  streetLabel={BorrowerInformationLabel.HomeStreetAddress}
                  initialValue={{
                    streetAddress: watcher.homeImprovementAddressStreetAddress || '',
                    city: watcher.homeImprovementAddressCity || '',
                    state: watcher.homeImprovementAddressState || '',
                    zip: watcher.homeImprovementAddressZip || '',
                  }}
                  inputChange={(name, value) => {
                    clearErrors(name as keyof BorrowerInformationFormValues);

                    if (name === 'homeImprovementAddressStreetAddress') {
                      setValue(name as keyof BorrowerInformationFormValues, removeIncorrectSymbols(value));
                    } else {
                      setValue(name as keyof BorrowerInformationFormValues, value);
                    }

                    if (formState.isSubmitted) {
                      trigger(name as keyof BorrowerInformationFormValues);
                    }
                  }}
                  getValues={getValues}
                />
              </div>
            )}
            <div>
              <div className={styles.radioContainer}>
                <div className={styles.subheader}>Do you wish to add a Co-borrower?</div>
                <FormControl>
                  <Controller
                    name={'loanType'}
                    control={control}
                    rules={{
                      validate: (value) => {
                        return value !== null;
                      },
                    }}
                    render={() => (
                      <RadioGroup
                        row
                        value={watcher.loanType}
                        onChange={(event) => {
                          const loanType = event.target.value as ApplicationType;

                          setValue('loanType', loanType);

                          if (loanType === ApplicationType.Joint) {
                            dispatch(setIsCoBorrowerManualFlow(true));
                          }

                          if (isSubmitted) {
                            trigger();
                          }
                        }}
                      >
                        <FormControlLabel
                          classes={{ label: styles.radioButtonLabel }}
                          value={ApplicationType.Joint}
                          control={
                            <Radio
                              classes={{
                                root: styles.radioButton,
                              }}
                            />
                          }
                          label={BorrowerInformationLabel.HomeImprovementYes}
                        />
                        <FormControlLabel
                          classes={{ label: styles.radioButtonLabel }}
                          value={ApplicationType.Individual}
                          control={
                            <Radio
                              classes={{
                                root: styles.radioButton,
                              }}
                            />
                          }
                          label={BorrowerInformationLabel.HomeImprovementNo}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </div>
              {!!errors?.loanType && (
                <FormHelperText className={styles.errorMessage}>
                  {BorrowerInformationLabel.HomeImprovementInformationIsRequired}
                </FormHelperText>
              )}
            </div>
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleOnSubmit} withArrow>
              Continue
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default BorrowerInformation;
